// File: src/pages/Faculties.jsx
// Description: Faculties page displaying all university faculties with departments and subjects

import React, { useState, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { facultiesData, facultyStats } from '../data/facultiesData';

const Faculties = () => {
  const { currentLanguage, getText } = useLanguage();
  const [expandedFaculty, setExpandedFaculty] = useState(null);
  const [expandedDepartment, setExpandedDepartment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Filter faculties based on search term and filter
  const filteredFaculties = useMemo(() => {
    let faculties = Object.values(facultiesData);
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      faculties = faculties.filter(faculty => 
        faculty.name[currentLanguage].toLowerCase().includes(term) ||
        faculty.description[currentLanguage].toLowerCase().includes(term) ||
        faculty.dean.name[currentLanguage].toLowerCase().includes(term) ||
        faculty.lecturers.some(lecturer => 
          lecturer.name[currentLanguage].toLowerCase().includes(term)
        )
      );
    }
    
    if (selectedFilter !== 'all') {
      faculties = faculties.filter(faculty => faculty.id === selectedFilter);
    }
    
    return faculties;
  }, [searchTerm, selectedFilter, currentLanguage]);

  const handleFacultyToggle = (facultyId) => {
    setExpandedFaculty(expandedFaculty === facultyId ? null : facultyId);
    setExpandedDepartment(null);
  };

  const handleDepartmentToggle = (departmentId) => {
    setExpandedDepartment(expandedDepartment === departmentId ? null : departmentId);
  };

  return (
    <div className="faculties-page">
      {/* Hero Section */}
      <section className="page-banner">
        <div className="banner-content">
          <h1 className='banner-title' style={{ marginTop: '5rem' }}>
            {getText({
              en: 'University Faculties',
              my: 'တက္ကသိုလ် မဟာဌာနများ'
            })}
          </h1>
          <p>
            {getText({
              en: 'Explore our distinguished faculties offering comprehensive Buddhist education and research',
              my: 'ပြည့်စုံသော ဗုဒ္ဓဘာသာ ပညာရေးနှင့် သုတေသနကို ပေးအပ်သည့် ကျွန်ုပ်တို့၏ ထင်ရှားသော မဟာဌာနများကို လေ့လာပါ'
            })}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="faculties-container">
        {/* Faculty Overview */}
        <section className="faculty-overview">
          <h2>
            {getText({
              en: 'Our Faculties',
              my: 'ကျွန်ုပ်တို့၏ မဟာဌာနများ'
            })}
          </h2>
          <p className="faculty-overview-description">
            {getText({
              en: 'Each faculty specializes in different aspects of Buddhist studies, from theoretical knowledge to practical application, language studies, and comparative religious research.',
              my: 'မဟာဌာနတစ်ခုစီသည် သီအိုရီအသိပညာမှ လက်တွေ့အသုံးချမှု၊ ဘာသာစကားလေ့လာမှုနှင့် နှိုင်းယှဉ်ဘာသာရေး သုတေသနအထိ ဗုဒ္ဓဘာသာ လေ့လာမှု၏ ကွဲပြားသော ရှုထောင့်များတွင် အထူးပြုသည်။'
            })}
          </p>
        </section>

        {/* Faculty Statistics */}
        <section className="faculty-stats">
          <div className="stat-item">
            <span className="stat-number">{facultyStats.totalFaculties}</span>
            <span className="stat-label">
              {getText({
                en: 'Faculties',
                my: 'မဟာဌာနများ'
              })}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{facultyStats.totalDepartments}</span>
            <span className="stat-label">
              {getText({
                en: 'Departments',
                my: 'ဌာနများ'
              })}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{facultyStats.totalSubjects}</span>
            <span className="stat-label">
              {getText({
                en: 'Subjects',
                my: 'ဘာသာရပ်များ'
              })}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{facultyStats.totalLecturers}</span>
            <span className="stat-label">
              {getText({
                en: 'Faculty Members',
                my: 'ဆရာတော်များ'
              })}
            </span>
          </div>
        </section>

        {/* Search and Filter */}
        <section className="faculty-filters">
          <div className="filter-row">
            <input
              type="text"
              className="search-input"
              placeholder={getText({
                en: 'Search faculties, lecturers, or subjects...',
                my: 'မဟာဌာန၊ ဆရာတော်များ သို့မဟုတ် ဘာသာရပ်များကို ရှာဖွေပါ...'
              })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="filter-select"
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
            >
              <option value="all">
                {getText({
                  en: 'All Faculties',
                  my: 'မဟာဌာန အားလုံး'
                })}
              </option>
              {Object.values(facultiesData).map(faculty => (
                <option key={faculty.id} value={faculty.id}>
                  {faculty.name[currentLanguage]}
                </option>
              ))}
            </select>
          </div>
        </section>

        {/* Faculties Grid */}
        <section className="faculties-grid">
          {filteredFaculties.length > 0 ? (
            filteredFaculties.map((faculty) => (
              <div key={faculty.id} className="faculty-card">
                {/* Faculty Header */}
                <div className="faculty-header">
                  <h3 className="faculty-title">{faculty.name[currentLanguage]}</h3>
                  <p className="faculty-subtitle">{faculty.description[currentLanguage]}</p>
                </div>

                {/* Faculty Content */}
                <div className="faculty-content">
                  {/* Dean Section */}
                  <div className="dean-section">
                    <h4 className="dean-title">
                      {getText({
                        en: 'Dean',
                        my: 'မဟာဌာနမှူး'
                      })}
                    </h4>
                    <div className="dean-info">
                      <p className="dean-name">{faculty.dean.name[currentLanguage]}</p>
                      <p className="dean-position">
                        {getText({
                          en: 'Dean',
                          my: 'မဟာဌာနမှူး'
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Lecturers Section */}
                  <div className="lecturers-section">
                    <h4 className="lecturers-title">
                      {getText({
                        en: 'Faculty Members',
                        my: 'ဆရာတော်များ'
                      })} ({faculty.lecturers.length})
                    </h4>
                    <div className="lecturers-grid">
                      {faculty.lecturers.slice(0, 6).map((lecturer, index) => (
                        <div key={index} className="lecturer-card">
                          <p className="lecturer-name">{lecturer.name[currentLanguage]}</p>
                          {lecturer.title && (
                            <p className="lecturer-position">{lecturer.title[currentLanguage]}</p>
                          )}
                        </div>
                      ))}
                      {faculty.lecturers.length > 6 && (
                        <div className="lecturer-card more-lecturers">
                          <p>+{faculty.lecturers.length - 6} {getText({
                            en: 'more',
                            my: 'ထပ်မံ'
                          })}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Departments Section */}
                  <div className="departments-section">
                    <h4 className="departments-title">
                      {getText({
                        en: 'Departments',
                        my: 'ဌာနများ'
                      })}
                    </h4>
                    <div className="departments-list">
                      {Object.entries(faculty.departments).map(([deptId, department]) => (
                        <div key={deptId} className="department-tag">
                          {department.name[currentLanguage]}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Subjects Section */}
                  <div className="subjects-section">
                    <h4 className="subjects-title">
                      {getText({
                        en: 'Sample Subjects',
                        my: 'နမူနာ ဘာသာရပ်များ'
                      })}
                    </h4>
                    <div className="subjects-grid">
                      {Object.values(faculty.departments).slice(0, 2).map((department, deptIndex) => 
                        department.subjects.slice(0, 3).map((subject, subjectIndex) => (
                          <div key={`${deptIndex}-${subjectIndex}`} className="subject-item">
                            {subject.name[currentLanguage]}
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Expand Button */}
                  <button
                    className="expand-btn"
                    onClick={() => handleFacultyToggle(faculty.id)}
                  >
                    {expandedFaculty === faculty.id 
                      ? getText({
                          en: 'Show Less',
                          my: 'လျှော့ပြရန်'
                        })
                      : getText({
                          en: 'View Details',
                          my: 'အသေးစိတ်ကြည့်ရန်'
                        })
                    }
                  </button>

                  {/* Expanded Content */}
                  {expandedFaculty === faculty.id && (
                    <div className="faculty-expanded">
                      {/* All Lecturers */}
                      <div className="all-lecturers">
                        <h5>
                          {getText({
                            en: 'All Faculty Members',
                            my: 'ဆရာတော် အားလုံး'
                          })}
                        </h5>
                        <div className="lecturers-grid">
                          {faculty.lecturers.map((lecturer, index) => (
                            <div key={index} className="lecturer-card">
                              <p className="lecturer-name">{lecturer.name[currentLanguage]}</p>
                              {lecturer.title && (
                                <p className="lecturer-position">{lecturer.title[currentLanguage]}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* All Departments with Subjects */}
                      <div className="all-departments">
                        <h5>
                          {getText({
                            en: 'Departments & Subjects',
                            my: 'ဌာနများနှင့် ဘာသာရပ်များ'
                          })}
                        </h5>
                        {Object.entries(faculty.departments).map(([deptId, department]) => (
                          <div key={deptId} className="department-detail">
                            <h6 className="department-name">{department.name[currentLanguage]}</h6>
                            <div className="subjects-list">
                              {department.subjects.map((subject, index) => (
                                <div key={index} className="subject-detail">
                                  <span className="subject-code">{subject.code}</span>
                                  <span className="subject-name">{subject.name[currentLanguage]}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="no-faculties">
              <h3>
                {getText({
                  en: 'No faculties found',
                  my: 'မဟာဌာနများ မတွေ့ရှိပါ'
                })}
              </h3>
              <p>
                {getText({
                  en: 'Try adjusting your search terms or filters.',
                  my: 'သင်၏ ရှာဖွေမှု စကားလုံးများ သို့မဟုတ် စစ်ထုတ်မှုများကို ပြင်ဆင်ကြည့်ပါ။'
                })}
              </p>
            </div>
          )}
        </section>

{/* Faculty Contact Information */}
<section className="faculty-contact">
  <div className="contact-info">
    <h3>
      {getText({
        en: 'Contact Faculty Administration',
        my: 'မဟာဌာန စီမံအုပ်ချုပ်ရေးနှင့် ဆက်သွယ်ရန်'
      })}
    </h3>
    <div className="contact-details">
      <div className="contact-item">
        <span className="contact-label">
          {getText({
            en: 'Office Hours:',
            my: 'ရုံးချိန်:'
          })}
        </span>
        <span className="contact-value">
          {getText({
            en: 'Monday - Friday: 9:00 AM - 4:00 PM',
            my: 'တနင်္လာ - သောကြာ: နံနက် ၉:၀၀ - ညနေ ၄:၀၀'
          })}
        </span>
      </div>
      <div className="contact-item">
        <span className="contact-label">
          {getText({
            en: 'Phone:',
            my: 'ဖုန်း:'
          })}
        </span>
        <span className="contact-value">09 667 654455</span>
      </div>
      <div className="contact-item">
        <span className="contact-label">
          {getText({
            en: 'Email:',
            my: 'အီးမေးလ်:'
          })}
        </span>
        <span className="contact-value">faculties@university.edu.mm</span>
      </div>
    </div>
  </div>
</section>

{/* Academic Calendar Notice */}
<section className="academic-notice">
  <div className="notice-content">
    <h4>
      {getText({
        en: 'Academic Information',
        my: 'ပညာရေးဆိုင်ရာ အချက်အလက်'
      })}
    </h4>
    <p>
      {getText({
        en: 'For detailed curriculum information, admission requirements, and course schedules, please contact the respective faculty offices or visit our academic affairs department.',
        my: 'သင်ရိုးညွှန်းတမ်း အသေးစိတ်အချက်အလက်များ၊ ဝင်ခွင့်လိုအပ်ချက်များနှင့် သင်ခန်းစာအချိန်ဇယားများအတွက် သက်ဆိုင်ရာ မဟာဌာနရုံးများသို့ ဆက်သွယ်ပါ သို့မဟုတ် ကျွန်ုပ်တို့၏ ပညာရေးရာဌာနသို့ လာရောက်ပါ။'
      })}
    </p>
  </div>
</section>
</div>
</div>
);
};

export default Faculties;